import { useFormikContext } from 'formik'
import { useEffect, useRef } from 'react'
import { FORM_FIELDS } from './constants'

export const LICENSE_HYDRATION = {
  SKIP: 'skip',
  OWN: 'own',
  APPLY: 'apply',
  KEEP: 'keep',
}

export const getLicenseIdsKey = (licenseIds = []) => [...licenseIds].filter(Boolean).sort().join(',')

/**
 * Decide whether incoming server license IDs may replace the form value.
 * Hydration is tracked separately from the license key so an empty initial
 * selection is not mistaken for "the user has not edited".
 */
export const getLicenseHydrationDecision = ({
  currentLicenseKey,
  assignedLicenseIdsKey,
  hasHydrated,
  userOwnsLicenses,
  lastHydratedLicenseKey,
}) => {
  if (userOwnsLicenses) {
    return { action: LICENSE_HYDRATION.SKIP }
  }

  if (!hasHydrated) {
    if (currentLicenseKey && currentLicenseKey !== assignedLicenseIdsKey) {
      return { action: LICENSE_HYDRATION.OWN }
    }
    if (currentLicenseKey !== assignedLicenseIdsKey) {
      return { action: LICENSE_HYDRATION.APPLY, lastHydratedLicenseKey: assignedLicenseIdsKey }
    }
    return { action: LICENSE_HYDRATION.KEEP, lastHydratedLicenseKey: assignedLicenseIdsKey }
  }

  if (currentLicenseKey !== lastHydratedLicenseKey) {
    return { action: LICENSE_HYDRATION.OWN }
  }

  if (currentLicenseKey !== assignedLicenseIdsKey) {
    return { action: LICENSE_HYDRATION.APPLY, lastHydratedLicenseKey: assignedLicenseIdsKey }
  }

  return { action: LICENSE_HYDRATION.KEEP, lastHydratedLicenseKey: assignedLicenseIdsKey }
}

const useHydrateAssignedLicenses = (assignedLicenseIds, employeeId, onLicensesApplied) => {
  const { setFieldValue, values } = useFormikContext()
  const hasHydratedRef = useRef(false)
  const userOwnsLicensesRef = useRef(false)
  const lastHydratedLicenseKeyRef = useRef('')
  const lastEmployeeIdRef = useRef(employeeId)
  const assignedLicenseIdsKey = getLicenseIdsKey(assignedLicenseIds)

  useEffect(() => {
    if (lastEmployeeIdRef.current !== employeeId) {
      hasHydratedRef.current = false
      userOwnsLicensesRef.current = false
      lastHydratedLicenseKeyRef.current = ''
      lastEmployeeIdRef.current = employeeId
    }

    const currentLicenseKey = getLicenseIdsKey(values[FORM_FIELDS.LICENSES])
    const decision = getLicenseHydrationDecision({
      currentLicenseKey,
      assignedLicenseIdsKey,
      hasHydrated: hasHydratedRef.current,
      userOwnsLicenses: userOwnsLicensesRef.current,
      lastHydratedLicenseKey: lastHydratedLicenseKeyRef.current,
    })

    const nextLicenseIds = assignedLicenseIds || []

    if (decision.action === LICENSE_HYDRATION.SKIP || decision.action === LICENSE_HYDRATION.OWN) {
      if (decision.action === LICENSE_HYDRATION.OWN) {
        userOwnsLicensesRef.current = true
        hasHydratedRef.current = true
      }
      // Form value stays user-authored; baseline must still track the server assignments.
      onLicensesApplied?.(nextLicenseIds)
      return
    }

    hasHydratedRef.current = true
    if (decision.lastHydratedLicenseKey != null) {
      lastHydratedLicenseKeyRef.current = decision.lastHydratedLicenseKey
    }

    if (decision.action === LICENSE_HYDRATION.APPLY) {
      setFieldValue(FORM_FIELDS.LICENSES, nextLicenseIds, false)
    }
    onLicensesApplied?.(nextLicenseIds)
    // Read current form values only when server licenses or employee change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedLicenseIdsKey, assignedLicenseIds, employeeId, setFieldValue, onLicensesApplied])
}

export default useHydrateAssignedLicenses
